import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { 
  User, 
  Mail, 
  Shield, 
  CreditCard, 
  Calendar,
  Save,
  Eye,
  EyeOff,
  CheckCircle,
  Copy,
  Check,
  Terminal,
  Server,
  RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import TelegramConfigCard from "@/components/settings/TelegramConfigCard";
import { isFeatureAvailable } from "@/lib/featureFlags";

export default function ProfilePage({ user, setUser, onLogout }) {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [tokenCopied, setTokenCopied] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  useEffect(() => {
    if (user) {
      setForm(prev => ({
        ...prev,
        name: user.name || "",
        email: user.email || "",
      }));
    }
  }, [user]);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updateData = { name: form.name };
      
      // If changing password
      if (form.newPassword) {
        if (form.newPassword !== form.confirmPassword) {
          toast.error("Las contraseñas no coinciden");
          setLoading(false);
          return;
        }
        if (form.newPassword.length < 6) {
          toast.error("La contraseña debe tener al menos 6 caracteres");
          setLoading(false);
          return;
        }
        updateData.current_password = form.currentPassword;
        updateData.new_password = form.newPassword;
      }

      const response = await api.patch("/auth/profile", updateData);
      
      // Update local user state
      if (setUser && response.data.user) {
        setUser(response.data.user);
        localStorage.setItem("user", JSON.stringify(response.data.user));
      }
      
      toast.success("Perfil actualizado correctamente");
      
      // Clear password fields
      setForm(prev => ({
        ...prev,
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar perfil");
    } finally {
      setLoading(false);
    }
  };

  const getPlanBadgeColor = (plan) => {
    switch (plan) {
      case "enterprise":
      case "enterprise_annual":
        return "bg-purple-500/20 text-purple-400 border-purple-500/30";
      case "pro":
        return "bg-[#34C759]/20 text-[#34C759] border-[#34C759]/30";
      default:
        return "bg-[#71717A]/20 text-[#71717A] border-[#71717A]/30";
    }
  };

  const getPlanName = (plan) => {
    switch (plan) {
      case "enterprise":
        return "Enterprise";
      case "enterprise_annual":
        return "Enterprise Anual";
      case "pro":
        return "Pro";
      default:
        return "Free";
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="top-bar">
        <div>
          <h1 className="font-heading text-xl font-bold text-[#F4F4F5]">
            Mi Perfil
          </h1>
          <p className="text-sm text-[#71717A]">
            Gestiona tu información personal y seguridad
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <div className="lg:col-span-1">
          <div className="bg-[#111111] border border-white/[0.08] rounded-sm p-6">
            {/* Avatar */}
            <div className="flex flex-col items-center mb-6">
              <div className="w-24 h-24 rounded-full bg-[#1A1A1A] border-2 border-[#34C759] flex items-center justify-center mb-4">
                <User className="w-12 h-12 text-[#34C759]" />
              </div>
              <h2 className="text-xl font-bold text-[#F4F4F5]">{user?.name}</h2>
              <p className="text-sm text-[#71717A]">{user?.email}</p>
            </div>

            {/* Info Cards */}
            <div className="space-y-4">
              <div className="p-4 bg-[#0A0A0A] rounded-sm border border-white/[0.05]">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-[#34C759]" />
                  <div>
                    <p className="text-xs text-[#71717A]">Rol</p>
                    <p className="font-medium text-[#F4F4F5] capitalize">{user?.role || "Operador"}</p>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-[#0A0A0A] rounded-sm border border-white/[0.05]">
                <div className="flex items-center gap-3">
                  <CreditCard className="w-5 h-5 text-[#3B82F6]" />
                  <div>
                    <p className="text-xs text-[#71717A]">Plan</p>
                    <span className={`px-2 py-1 text-xs font-medium rounded-sm border ${getPlanBadgeColor(user?.subscription_plan)}`}>
                      {getPlanName(user?.subscription_plan)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-[#0A0A0A] rounded-sm border border-white/[0.05]">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-[#71717A]" />
                  <div>
                    <p className="text-xs text-[#71717A]">Miembro desde</p>
                    <p className="font-medium text-[#F4F4F5]">
                      {user?.created_at 
                        ? new Date(user.created_at).toLocaleDateString('es-CL', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })
                        : "N/A"
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Edit Form */}
        <div className="lg:col-span-2">
          <form onSubmit={handleUpdateProfile} className="space-y-6">
            {/* Personal Info */}
            <div className="bg-[#111111] border border-white/[0.08] rounded-sm p-6">
              <h3 className="text-lg font-semibold text-[#F4F4F5] mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-[#34C759]" />
                Información Personal
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-group">
                  <Label>Nombre Completo</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Tu nombre"
                    className="input-tactical"
                    data-testid="profile-name-input"
                  />
                </div>

                <div className="form-group">
                  <Label>Email</Label>
                  <div className="relative">
                    <Input
                      value={form.email}
                      disabled
                      className="input-tactical opacity-60"
                      data-testid="profile-email-input"
                    />
                    <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
                  </div>
                  <p className="text-xs text-[#71717A] mt-1">
                    El email no se puede cambiar
                  </p>
                </div>
              </div>
            </div>

            {/* Security */}
            <div className="bg-[#111111] border border-white/[0.08] rounded-sm p-6">
              <h3 className="text-lg font-semibold text-[#F4F4F5] mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#FFCC00]" />
                Cambiar Contraseña
              </h3>
              <p className="text-sm text-[#71717A] mb-4">
                Deja estos campos vacíos si no quieres cambiar tu contraseña
              </p>

              <div className="space-y-4">
                <div className="form-group">
                  <Label>Contraseña Actual</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={form.currentPassword}
                      onChange={(e) => setForm({ ...form, currentPassword: e.target.value })}
                      placeholder="••••••••"
                      className="input-tactical pr-10"
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-[#F4F4F5]"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-group">
                    <Label>Nueva Contraseña</Label>
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={form.newPassword}
                      onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
                      placeholder="••••••••"
                      className="input-tactical"
                      data-testid="new-password-input"
                    />
                  </div>

                  <div className="form-group">
                    <Label>Confirmar Contraseña</Label>
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={form.confirmPassword}
                      onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                      placeholder="••••••••"
                      className="input-tactical"
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Agent Token - Only if feature available */}
            {isFeatureAvailable('local_agent') && (
            <div className="bg-[#111111] border border-white/[0.08] rounded-sm p-6">
              <h3 className="text-lg font-semibold text-[#F4F4F5] mb-4 flex items-center gap-2">
                <Server className="w-5 h-5 text-[#34C759]" />
                Token de Agente Local
              </h3>
              <p className="text-sm text-[#71717A] mb-4">
                Usa este token para conectar el agente local que detecta equipos RF en tu red
              </p>

              <div className="bg-[#0A0A0A] rounded-sm p-4 border border-white/[0.05]">
                <div className="flex items-center gap-2 mb-3">
                  <Terminal className="w-4 h-4 text-[#71717A]" />
                  <span className="text-xs text-[#71717A]">Tu token JWT</span>
                </div>
                
                <div className="flex gap-2">
                  <div className="flex-1 bg-[#1A1A1A] rounded p-3 font-mono text-xs text-[#A1A1AA] overflow-hidden break-all max-h-20 overflow-y-auto">
                    {showToken 
                      ? localStorage.getItem('token') 
                      : '••••••••••••••••••••••••••••••••••••••••'
                    }
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button 
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowToken(!showToken)}
                      className="px-2"
                    >
                      {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button 
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const token = localStorage.getItem('token');
                        if (token) {
                          navigator.clipboard.writeText(token);
                          setTokenCopied(true);
                          toast.success('Token copiado al portapapeles');
                          setTimeout(() => setTokenCopied(false), 2000);
                        }
                      }}
                      className="px-2"
                      data-testid="copy-agent-token-btn"
                    >
                      {tokenCopied ? <Check className="w-4 h-4 text-[#34C759]" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                
                <div className="mt-4 p-3 bg-[#1A1A1A] rounded border border-[#FFCC00]/20">
                  <p className="text-xs text-[#FFCC00] mb-2 font-medium">
                    Cómo usar el agente:
                  </p>
                  <pre className="text-xs text-[#71717A] font-mono overflow-x-auto">
{`pip install aiohttp websockets
python avx_agent.py -t TU_TOKEN`}
                  </pre>
                </div>
                
                <p className="text-xs text-[#52525B] mt-3">
                  El token expira cuando cierras sesión. Si el agente deja de funcionar, vuelve a copiar el token después de iniciar sesión.
                </p>
              </div>
            </div>
            )}

            {/* Telegram Alerts Config - Only if feature available */}
            {isFeatureAvailable('telegram_alerts') && <TelegramConfigCard />}

            {/* Save Button */}
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={loading}
                className="btn-tactical btn-tactical-primary"
                data-testid="save-profile-btn"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Guardar Cambios
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
}
